import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApi, API } from "@/lib/api";

const readStream = async (res, onEvent) => {
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.filter((p) => p.startsWith("data: ")).forEach((p) => onEvent(JSON.parse(p.slice(6))));
  }
};

export const useCalibrator = (onScenario) => {
  const api = useApi();
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState("claude");

  const newChat = () => { if (!busy) { setSessionId(null); setMsgs([]); } };
  const open = async (id) => {
    if (busy) return;
    setLoading(true);
    try {
      const list = await api.get(`/ai/sessions/${id}`);
      setMsgs(list); setSessionId(id); setModel(list[0]?.model || "claude");
    }
    catch { setMsgs([]); setSessionId(null); }
    setLoading(false);
  };

  const ask = async (question) => {
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "", streaming: true }]);
    const patchLast = (fn) => setMsgs((m) => [...m.slice(0, -1), fn(m[m.length - 1])]);
    try {
      const res = await fetch(`${API}/ai/simulate`, {
        method: "POST", headers: { "Content-Type": "application/json", ...(await api.headers()) },
        body: JSON.stringify({ question, session_id: sessionId, model }),
      });
      if (res.status === 429) {
        const d = (await res.json()).detail;
        patchLast((l) => ({ ...l, content: d?.message || "Daily limit reached. Come back tomorrow.", limit: true }));
        throw new Error("limit");
      }
      await readStream(res, (ev) => {
        if (ev.type === "session") { setSessionId(ev.session_id); setModel(ev.model); }
        if (ev.type === "delta") patchLast((l) => ({ ...l, content: l.content + ev.content }));
        if (ev.type === "error") patchLast((l) => ({ ...l, content: ev.message }));
        if (ev.type === "done" && ev.scenario) { onScenario(ev.scenario); patchLast((l) => ({ ...l, scenario: ev.scenario })); }
      });
    } catch (e) {
      if (e.message !== "limit") patchLast((l) => ({ ...l, content: "Something went wrong. Try again." }));
    }
    patchLast((l) => ({ ...l, streaming: false }));
    setBusy(false);
    qc.invalidateQueries({ queryKey: ["ai-sessions"] });
    qc.invalidateQueries({ queryKey: ["ai-usage"] });
    qc.invalidateQueries({ queryKey: ["usage-history"] });
  };
  return { sessionId, msgs, busy, loading, ask, newChat, open, model, setModel };
};
